// @flow

import * as React from 'react';

export type DebounceOptions = {|
  leading?: boolean,
  trailing?: boolean,
  maxWait?: number,
|};

type InternalState = {|
  lastArgs?: {
    args: any[],
  },
  result?: any,
  lastCallTime: number,
  lastInvokeTime: number,
  timerId?: TimeoutID,
|};

const useDebouncedCallback = <T: (...args: any[]) => any>(
  func: T,
  wait: number,
  options?: DebounceOptions,
): T => {
  const { leading = false, trailing = true, maxWait } = options || {};
  const internalWait = maxWait ? Math.min(maxWait, wait) : wait;
  const state = React.useRef<InternalState>({
    lastCallTime: 0,
    lastInvokeTime: 0,
  });

  const debounced = React.useMemo(() => {
    const invoke = (time: number) => {
      if (!state.current.lastArgs) {
        throw new Error('Invocation before external call');
      }

      const { args } = state.current.lastArgs;
      state.current.lastArgs = undefined;
      state.current.lastInvokeTime = time;
      state.current.result = func(...args);
      return state.current.result;
    };

    const shouldInvoke = (time: number): boolean => {
      const timeSinceLastCall = time - state.current.lastCallTime;
      const timeSinceLastInvoke = time - state.current.lastInvokeTime;

      // Either this is the first call, activity has stopped and we're at the
      // trailing edge, the system time has gone backwards and we're treating
      // it as the trailing edge, or we've hit the `maxWait` limit.
      return (
        state.current.lastCallTime === 0 ||
        timeSinceLastCall >= internalWait ||
        timeSinceLastCall < 0 ||
        (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
      );
    };

    const trailingEdge = (time: number) => {
      state.current.timerId = undefined;

      // Only invoke if we have `lastArgs` which means `func` has been
      // debounced at least once.
      if (trailing && state.current.lastArgs) {
        return invoke(time);
      }
      state.current.lastArgs = undefined;
      return state.current.result;
    };

    const remainingWait = (time: number): number => {
      const timeSinceLastCall = time - state.current.lastCallTime;
      const timeSinceLastInvoke = time - state.current.lastInvokeTime;
      const timeWaiting = internalWait - timeSinceLastCall;

      return maxWait !== undefined
        ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
        : timeWaiting;
    };

    const timerExpired = () => {
      const time = Date.now();
      if (shouldInvoke(time)) {
        trailingEdge(time);
      }
      // Restart the timer.
      else {
        state.current.timerId = setTimeout(timerExpired, remainingWait(time));
      }
    };

    const leadingEdge = (time: number) => {
      // Reset any `maxWait` timer.
      state.current.lastInvokeTime = time;
      if (trailing) {
        // Start the timer for the trailing edge.
        state.current.timerId = setTimeout(timerExpired, internalWait);
      }
      // Invoke the leading edge.
      return leading ? invoke(time) : state.current.result;
    };

    const cancel = () => {
      if (state.current.timerId !== undefined) {
        clearTimeout(state.current.timerId);
      }
      state.current.lastInvokeTime = 0;
      state.current.lastArgs = undefined;
      state.current.lastCallTime = 0;
      state.current.timerId = undefined;
    };

    const flush = () =>
      state.current.timerId === undefined ? state.current.result : trailingEdge(Date.now());

    const result = (...args) => {
      const time = Date.now();
      const isInvoking = shouldInvoke(time);

      state.current.lastArgs = { args };
      state.current.lastCallTime = time;

      if (isInvoking) {
        if (state.current.timerId === undefined) {
          return leadingEdge(state.current.lastCallTime);
        }
        if (maxWait !== undefined) {
          // Handle invocations in a tight loop.
          clearTimeout(state.current.timerId);
          state.current.timerId = setTimeout(timerExpired, internalWait);
          return invoke(state.current.lastCallTime);
        }
      }
      if (state.current.timerId === undefined) {
        state.current.timerId = setTimeout(timerExpired, internalWait);
      }
      return state.current.result;
    };

    result.cancel = cancel;
    result.flush = flush;

    return result;
  }, [func, leading, maxWait, trailing, internalWait]);
  return (debounced: any);
};

export default useDebouncedCallback;
